import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthService, FCMNotificationActionEnum, FCMNotificationActionGroupEnum } from './auth.service';
import { ServiceBase } from './service.base';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { NotificationTypes } from '../app.enums';
import { NotificationsService } from './shared/notifications.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class MessagingService extends ServiceBase {
  private api = '/api';
  currentMessage = new BehaviorSubject<any>(null);
  private unreadNotificationCount = new BehaviorSubject<number>(0);
  private unreadChatboxCount = new BehaviorSubject<number>(0);

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private afMessaging: AngularFireMessaging,
    private http: HttpClient,
    authService: AuthService,
    private notificationsService: NotificationsService,
    private transloco: TranslocoService,


  ) {
    super(authService);
    if (isPlatformBrowser(this.platformId)) {
      this.listenForMessages();
    }
  }

  // Get the unread notification count as an observable
  getUnreadNotificationCount() {
    return this.unreadNotificationCount.asObservable();
  }

  // Get the unread chatbox count as an observable
  getUnreadChatboxCount() {
    return this.unreadChatboxCount.asObservable();
  }

  // Save the FCM token to the backend server
  public saveToken(token: string): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const method: string = '/register-FCM-tokken';
    const url: string = environment.BASE_API_PATH + '/AppNotifications' + method;
    const body = { token };
    return this.http.post<any>(url, body, httpOptions);
  }

  // Request notification permission from the user
  requestPermission() {
    // Skip on server-side rendering
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.afMessaging.requestToken.pipe(take(1)).subscribe(
      (token) => {
        if (token) {
          this.saveToken(token).subscribe(
            (response) => console.log('Token saved successfully:', response),
            (error) => console.error('Error saving FCM token:', error)
          );
        } else {
          console.error('No FCM token received.');
        }
      },
      (error) => console.error('Permission Denied', error)
    );

    // Notification API is only available in browser
    if (typeof Notification !== 'undefined') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log("Notification permission granted.");
        } else {
          console.error('Notification permission denied.');
        }
      });
    }
  }
  receiveMessage() {
    this.afMessaging.messages.subscribe((message: any) => {
        console.log(message, 'message');
        this.currentMessage.next(message);
        if (message && message.data) {
            const actionType = message.data.actionType;
            const senderId = message.data.entityId; // Assuming senderId is part of the message data
            const receiverId = this.authService.getUserId(); // Assuming you have a method to get the current user's ID

            if (senderId !== receiverId) {
                if (actionType === FCMNotificationActionEnum.ChatBox ) {
                    this.playNotificationSound(this.transloco.translate('messagingService.newChatMessageTitle'), this.transloco.translate('messagingService.newChatMessageBody'));
                } else if (
                    actionType === FCMNotificationActionEnum.Proposal ||
                    actionType === FCMNotificationActionEnum.AcceptProposal ||
                    actionType === FCMNotificationActionEnum.ProductSold ||
                    actionType === FCMNotificationActionEnum.ProductReturn ||
                    actionType === FCMNotificationActionEnum.ConnectionRequest ||
                    actionType === FCMNotificationActionEnum.ConnectionAccepted ||
                    actionType === FCMNotificationActionEnum.ConnectionRejected ||
                    actionType === FCMNotificationActionEnum.ConsignmentDispatch ||
                    actionType === FCMNotificationActionEnum.ConsignmentAccepted ||
                    actionType === FCMNotificationActionEnum.RejectProposal
                ) {
                    this.playNotificationSound(this.transloco.translate('messagingService.newNotificationTitle'), this.transloco.translate('messagingService.newNotificationBody'));
                }
            }
        }
    });
}

private playNotificationSound(title: string, body: string) {
  // Audio API is only available in browser
  if (isPlatformBrowser(this.platformId)) {
    const audio = new Audio('assets/audio-tune/Whatsapp Message Ringtone Download - MobCup.Com.Co.mp3');
    audio.play().catch(err => console.error('Audio play failed:', err));
  }
  this.notificationsService.showNotification(title, body, NotificationTypes.Info);
} 
 

  // Listen for messages from the service worker
  private listenForMessages() {
    // Service Worker API is only available in browser
    if (isPlatformBrowser(this.platformId) && typeof navigator !== 'undefined' && navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        this.currentMessage.next(event.data);
      });
    }
  }

  // Fetch the count of unread notifications
  public CountUnreadNotificaion(): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/fetch-unread-notificaion-count`;
    return this.http.get<any>(url, httpOptions);
  }

  // Fetch the count of unread chatbox messages
  public CountUnreadchatbox(): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/fetch-unread-chatbox-count`;
    return this.http.get<any>(url, httpOptions);
  }

  // Fetch the top N notifications from the server
  public fetchNotifications(topN: number): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/fetch-notification/${topN}`;
    return this.http.get<any>(url, httpOptions);
  }

  // Fetch the top N chatbox notifications from the server
  public fetchchatboxnotification(topN: number): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/fetch-chatbox-notification/${topN}`;
    return this.http.get<any>(url, httpOptions);
  }

  // Mark a specific notification as read
  public markasreadNotificaion(EntityId: string): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/mark-as-read/${EntityId}`;
    return this.http.post<any>(url, {}, httpOptions);
  }

  // Mark a specific chatbox notification as read
  public markasreadchatboxNotificaion(SenderId: string): Observable<any> {
    const httpOptions = this.RequestHeaders();
    const url: string = `${environment.BASE_API_PATH}/AppNotifications/mark-as-read-chatbox-notification/${SenderId}`;
    return this.http.post<any>(url, {}, httpOptions);
  }

  public handleNotificationRead(EntityId: string): void {
    const httpOptions = this.RequestHeaders();
    // Step 1: Mark Notification as Read
    this.http.post<any>(`${environment.BASE_API_PATH}/AppNotifications/mark-as-read/${EntityId}`, {}, httpOptions)
      .subscribe(() => {
        // Step 2: Fetch Updated Unread Notification Count
        this.http.get<number>(`${environment.BASE_API_PATH}/AppNotifications/fetch-unread-notificaion-count`, httpOptions)
          .subscribe(count => {
            this.unreadNotificationCount.next(count); // Update the shared service state
          });
      });
  }

  public handleMarkasReadByActionType(groupName: string): void {
    const httpOptions = this.RequestHeaders();
    // Step 1: Mark Notification as Read
    this.http.post<any>(`${environment.BASE_API_PATH}/AppNotifications/mark-as-read-by-notification-action-groupname/${groupName}`, {}, httpOptions)
      .subscribe(() => {
        // Step 2: Fetch Updated Unread Notification Count
        this.http.get<number>(`${environment.BASE_API_PATH}/AppNotifications/fetch-unread-notificaion-count`, httpOptions)
          .subscribe(count => {
            this.unreadNotificationCount.next(count); // Update the shared service state
          });
      });
  }

  public handleMarkasReadBychat(SenderId: string): void {
    const httpOptions = this.RequestHeaders();
    // Step 1: Mark Notification as Read
    this.http.post<any>(`${environment.BASE_API_PATH}/AppNotifications/mark-as-read-chatbox-notification/${SenderId}`, {}, httpOptions)
      .subscribe(() => {
        this.http.get<number>(`${environment.BASE_API_PATH}/AppNotifications/fetch-unread-chatbox-count`, httpOptions)
          .subscribe(count => {
            this.unreadChatboxCount.next(count);
          });
      });
  }
}